import { createFileRoute, Outlet, useNavigate, useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { AppSidebar } from "@/components/app/sidebar";
import { AuthDebugPanel } from "@/components/app/auth-debug-panel";
import { UIModeProvider } from "@/lib/ui-mode";
import { Topbar, FloatingAIButton } from "@/components/app/topbar";

let hasRenderedAuthenticatedLayout = false;
let authGuardMountCount = 0;
let shellMountCount = 0;

export const Route = createFileRoute("/_authenticated")({
  // Auth-State (user, session) ist nur im Browser verfügbar. SSR würde
  // immer ohne User rendern und beim Hydrate zu Mismatches führen
  // (Topbar-Email, Sidebar-Badges, etc.).
  ssr: false,
  component: AuthLayout,
});

function AuthLayout() {
  const { user, loading, initError, retry } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Remount-Protokoll: jeder neue Mount erhöht den Counter. Wenn du im
  // Console-Log "[auth-guard] mount #N" mehrfach hintereinander siehst,
  // wird der Guard zerstört und neu erzeugt → Ursache fürs Flackern.
  useEffect(() => {
    authGuardMountCount += 1;
    const id = authGuardMountCount;
    console.info(
      `[auth-guard] mount #${id} path=${location.pathname} hadUser=${hadUserRef.current}`,
    );
    return () => {
      console.info(`[auth-guard] unmount #${id} path=${location.pathname}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sobald wir einmal einen User hatten, gilt das Layout als "stabil sichtbar".
  // Spätere kurzzeitige loading=true (Token-Refresh, Re-Subscribe, HMR) sollen
  // die Sidebar nicht mehr ausblenden, sonst flackert "InboxPulse".
  const hadUserRef = useRef(hasRenderedAuthenticatedLayout);
  if (user) {
    hadUserRef.current = true;
    hasRenderedAuthenticatedLayout = true;
  }

  // Failsafe: if the session check stalls (e.g. Supabase getSession hängt),
  // bail out to /login instead of an endless loading state.
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setTimedOut(true), 4000);
    return () => {
      clearTimeout(t);
    };
  }, [loading]);

  useEffect(() => {
    // Bei Init-Fehler/Timeout NICHT redirecten — Fehlermeldung mit Retry zeigen.
    if (initError) return;
    const shouldRedirect = (!loading && !user) || (timedOut && !user);
    if (shouldRedirect) {
      console.warn(
        `[auth-guard] No session for ${location.pathname} (timedOut=${timedOut}) — redirecting to /login`,
      );
      navigate({ to: "/login", replace: true });
    }
  }, [user, loading, timedOut, initError, navigate, location.pathname]);

  if (initError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <div className="max-w-md w-full rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <div className="font-semibold text-destructive mb-1">
            Session konnte nicht initialisiert werden
          </div>
          <div className="text-foreground/80 break-words">{initError}</div>
        </div>
        <div className="flex gap-2">
          <button
            className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm hover:opacity-90"
            onClick={() => {
              setTimedOut(false);
              retry();
            }}
          >
            Erneut versuchen
          </button>
          <button
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
            onClick={() => navigate({ to: "/login", replace: true })}
          >
            Zum Login
          </button>
        </div>
        <AuthDebugPanel />
      </div>
    );
  }

  const appShell = (content: ReactNode) => (
    <UIModeProvider>
      <div className="flex min-h-screen bg-background">
        <AppSidebar />
        <div className="flex-1 min-w-0 flex flex-col">
          <Topbar />
          <main className="flex-1 min-w-0">
            <ShellContent>{content}</ShellContent>
          </main>
        </div>
        <FloatingAIButton />
      </div>
    </UIModeProvider>
  );

  // Nie den kompletten App-Shell durch einen Vollbild-Loader ersetzen:
  // genau dadurch verschwand "InboxPulse" bei langsamer Session-Initialisierung.
  // Aber: solange wir noch nie einen User hatten, dürfen Kind-Routen NICHT
  // mounten — sonst feuern sie serverFn-Calls ohne Auth-Header (401 → Blank).
  if (!hadUserRef.current) {
    return appShell(
      <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
        Session wird geladen…
      </div>,
    );
  }
  return appShell(<Outlet />);
}

/**
 * Wrapper rund um den Outlet, der jeden Mount/Unmount protokolliert.
 * Routenwechsel sollen den Inhalt austauschen, ABER nicht den Shell —
 * d.h. `[shell-content] mount` darf bei reiner Navigation NICHT in einer
 * Schleife auftauchen; tut es das, wird der Shell von außen remounted.
 */
function ShellContent({ children }: { children: ReactNode }) {
  useEffect(() => {
    shellMountCount += 1;
    const id = shellMountCount;
    console.info(`[shell-content] mount #${id}`);
    return () => {
      console.info(`[shell-content] unmount #${id}`);
    };
  }, []);
  return <>{children}</>;
}
